import {
  BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES,
  DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID,
  normalizeNavigationPageDisplayStates,
} from "../../../lib/navigationPageState";
import type {
  NavigationPageDisplayClasses,
  NavigationPageDisplaySettings,
  NavigationPageDisplayState,
  NavigationPageItem,
  NavigationPageItemVariant,
} from "../../../types/blocks/navigation/NavigationPageOption";
import {
  NAV_FIELDS,
  getField,
  isSet,
  setField,
  type NavigationFieldKey,
  type NavigationFieldTarget,
} from "./fieldRegistry";

export type LayerKey = "base" | "link" | "state" | "variant";

export interface LayerRef {
  key: LayerKey;
  label: string;
  src: NavigationFieldTarget;
}

export interface ResolvedEditorLayers {
  resolved: Partial<NavigationPageDisplaySettings> & {
    classes: Required<NavigationPageDisplayClasses>;
  };
  winner: Partial<Record<NavigationFieldKey, LayerRef>>;
}

const EMPTY_CLASSES: Required<NavigationPageDisplayClasses> = {
  card: "",
  description: "",
  icon: "",
  interactive: "",
};

export function variantStateId(
  variant: Pick<NavigationPageItemVariant, "displayStateId" | "preset">,
): string {
  return variant.displayStateId
    ?? variant.preset
    ?? DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID;
}

export function findDisplayState(
  states: NavigationPageDisplayState[] | undefined,
  stateId: string | undefined,
): NavigationPageDisplayState {
  const displayStates = states?.length
    ? states
    : BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES;
  return displayStates.find((state) => state.id === stateId)
    ?? displayStates.find((state) => state.id === DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID)
    ?? BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES[0];
}

export function editorDisplayStates(
  configuredStates: unknown,
): NavigationPageDisplayState[] {
  const normalized = normalizeNavigationPageDisplayStates(configuredStates);
  if (normalized.length) return normalized;

  const fallback = BUILT_IN_NAVIGATION_PAGE_DISPLAY_STATES[0];
  return [{
    ...fallback,
    classes: { ...fallback.classes },
  }];
}

export function editorLayers(
  item: NavigationPageItem,
  configuredStates: unknown,
  simulatedVariant: NavigationPageItemVariant | null,
): LayerRef[] {
  const displayStates = editorDisplayStates(configuredStates);
  const baseState = findDisplayState(
    displayStates,
    item.displayStateId ?? DEFAULT_NAVIGATION_PAGE_DISPLAY_STATE_ID,
  );
  const layers: LayerRef[] = [
    {
      key: "base",
      label: baseState.name || "Base state",
      src: baseState,
    },
    {
      key: "link",
      label: item.title || "This link",
      src: item,
    },
  ];

  if (simulatedVariant) {
    const state = findDisplayState(displayStates, variantStateId(simulatedVariant));
    layers.push({
      key: "state",
      label: state.name || "Matched state",
      src: state,
    });
    layers.push({
      key: "variant",
      label: simulatedVariant.name || "Variant",
      src: simulatedVariant,
    });
  }

  return layers;
}

export function resolveLayers(layers: readonly LayerRef[]): ResolvedEditorLayers {
  let resolved: Partial<NavigationPageDisplaySettings> & {
    classes: Required<NavigationPageDisplayClasses>;
  } = {
    classes: { ...EMPTY_CLASSES },
  };
  const winner: Partial<Record<NavigationFieldKey, LayerRef>> = {};

  for (const layer of layers) {
    for (const field of NAV_FIELDS) {
      const value = getField(layer.src, field);
      if (!isSet(value)) continue;
      resolved = setField(resolved, field, value);
      winner[field.key] = layer;
    }
  }

  resolved.classes = {
    ...EMPTY_CLASSES,
    ...(resolved.classes ?? {}),
  };

  return { resolved, winner };
}
